import { RelatedTasksTabProps } from "./RelatedTasksTab.tsx";
import { deleteHttp } from "../../common/api/apiShared.ts";
import { RelatedTasksTableModel, RelatedTaskViewModel, translateTaskOrigin } from "./RelatedTasksTab.types.ts";
import { useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation.ts";
import { AnnualWheelMonthEnum, TaskStateEnum } from "../../annualWheel/AnnualWheel.types.ts";
import { isNullOrWhitespace, setFirstLetterToLowerCase } from "../../common/stringOperations.ts";
import { EntityType } from "../../common/Common.types.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { usePlanContext } from "../../auth/planProvider/PlanProvider.tsx";
import { FilterOption } from "../../common/components/filterPopover/FilterPopover.types.ts";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, matchesDateFilter, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { usePanes, useRefetchWhenPaneSaves } from "../../main/panes/usePanes";
import ActivityPane from "../../annualWheel/activityPane/ActivityPane";
import TaskPane from "../taskPane/TaskPane";

export function useRelatedTasksTab(props: RelatedTasksTabProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString, translateEntityType } = useTranslation();
    const { open: openPane } = usePanes();

    const [selectedRelationToRemove, setSelectedRelationToRemove] = useState<RelatedTasksTableModel | undefined>(undefined);
    const [selectedTaskToDelete, setSelectedTaskToDelete] = useState<RelatedTasksTableModel | undefined>(undefined);
    const [showAddExistingTasksDialog, setShowAddExistingTasksDialog] = useState(false);

    const { selectFilters, dateFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState("entity-task-tab");

    const { isLoading, data, refetch } = useGetRelatedTaskData(props.forEntityId, props.customApiUrl);

    const taskIds = (selectFilters.task ?? []).filter((id) => (data ?? []).some((x) => x.id === id));
    const responsibleIds = (selectFilters.responsible ?? []).filter((id) => (data ?? []).some((x) => x.responsible?.id === id));
    const taskStructures = (selectFilters.taskStructure ?? []).filter((ts) => (data ?? []).some((x) => x.taskOrigin.toString() === ts));
    const taskStates = (selectFilters.taskState ?? []).filter((ts) => (data ?? []).some((x) => x.taskState.toString() === ts));
    const deadlineFilter = dateFilters.deadline ?? undefined;

    function openActivityInReadMode(annualWheelActivityId: string) {
        openPane(ActivityPane, { mode: "read", annualWheelActivityId });
    }

    function openTask(taskId: string) {
        openPane(TaskPane, { taskId });
    }

    // The panes live above the router, so they have no onSave prop to call the way the dialogs'
    // onDialogClose did. They reach back through this instead, once, as they close. onUpdate goes with it
    // so the entity page's task count follows the list.
    useRefetchWhenPaneSaves(() => {
        refetch();
        props.onUpdate?.();
    });

    const removeMutation = useDotLegalMutation(deleteApi);
    const removeRelation = async (relationId: string) => {
        await removeMutation.mutateAsync(relationId, {
            onSuccess: () => {
                refetch();

                if (props.onUpdate) {
                    props.onUpdate();
                }

                snackbar.show(translateString("relationRemoved"), "success");
                setSelectedRelationToRemove(undefined);
            },
        });
    };

    function getFilterDataOptions() {
        if (!data) {
            return [];
        }

        let filterOptions: Array<FilterOption> = [];

        filterOptions.push({
            type: 1,
            id: "task",
            value: taskIds,
            label: translateString("task"),
            fixedData: data.filter((item, index, self) => index === self.findIndex((i) => i.id === item.id)).map((x) => ({ id: x.id, name: x.title })),
        });

        filterOptions.push({
            type: 1,
            id: "responsible",
            value: responsibleIds,
            label: translateString("responsible"),
            fixedData: data
                .filter((x) => x.responsible)
                .map((x) => x.responsible)
                .filter((item, index, self) => index === self.findIndex((i) => i!.id === item!.id))
                .map((x) => {
                    return { id: x!.id, name: x!.name };
                }),
        });

        filterOptions.push({
            type: 1,
            id: "taskState",
            value: taskStates,
            label: translateString("status"),
            fixedData: data
                .filter((item, index, self) => index === self.findIndex((i) => i.taskState === item.taskState))
                .map((x) => {
                    return { id: x.taskState.toString(), name: translateString(setFirstLetterToLowerCase(TaskStateEnum[x.taskState].toString())) };
                }),
        });

        filterOptions.push({
            type: 3,
            id: "deadline",
            value: deadlineFilter,
            label: translateString("deadline"),
            hideNoDate: true,
        });

        if (props.showTaskOrigin) {
            let taskOrigins = data.map((x) => x.taskOrigin).filter((value, index, self) => self.indexOf(value) === index);

            filterOptions.push({
                type: 1,
                id: "taskStructure",
                value: taskStructures,
                label: translateString("taskType"),
                fixedData: taskOrigins.map((x) => {
                    return {
                        id: x.toString(),
                        name: translateTaskOrigin(x, translateString),
                    };
                }),
            });
        }

        return withOperators(filterOptions, filterOperators);
    }

    const relatedTasks = applyClientFilters(
        data ?? [],
        [
            { id: "task", candidate: (t) => t.id },
            { id: "responsible", candidate: (t) => t.responsible?.id },
            { id: "taskStructure", candidate: (t) => t.taskOrigin.toString() },
            { id: "taskState", candidate: (t) => t.taskState.toString() },
        ],
        { ...selectFilters, task: taskIds, responsible: responsibleIds, taskStructure: taskStructures, taskState: taskStates },
        {},
        filterOperators,
        (task) => matchesDateFilter(deadlineFilter, task.deadline)
    )!;

    const hasRelatedTasks = isLoading || (data?.length ?? 0) > 0;

    const hasActiveFilters =
        taskIds.length > 0 || responsibleIds.length > 0 || taskStructures.length > 0 || taskStates.length > 0 || deadlineFilter !== undefined;

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        isLoading,
        refetchTasks: refetch,
        data: convertRowsToTableModel(relatedTasks ?? []),
        selectedRelationToRemove,
        setSelectedRelationToRemove,
        removeRelation,
        openTask,
        selectedTaskToDelete,
        setSelectedTaskToDelete,
        showAddExistingTasksDialog,
        setShowAddExistingTasksDialog,
        openActivityInReadMode,
        mapSearchFilter: onFilterChange,
        getFilterDataOptions,
        hasRelatedTasks,
        hasActiveFilters,
    };

    async function deleteApi(relationId: string) {
        return await deleteHttp(`/relations/${relationId}`);
    }

    function convertRowsToTableModel(rows: Array<RelatedTaskViewModel>) {
        return rows.map((x) => {
            return {
                ...x,
                deadlineString: `${translateString(AnnualWheelMonthEnum[new Date(x.deadline).getMonth() + 1].toLocaleLowerCase())} ${new Date(x.deadline).getFullYear()}`,
                stateString: translateString(setFirstLetterToLowerCase(TaskStateEnum[x.taskState].toString())),
                entityTypeString: x.additionalEntityInfo ? translateEntityType(EntityType[x.additionalEntityInfo.entityType].toString()) : undefined,
                entityName: x.additionalEntityInfo ? x.additionalEntityInfo.additionalEntityName : undefined,
            };
        });
    }
}

export function useGetRelatedTaskData(forEntityId: string, customApiUrl?: string) {
    const { permissions } = useUserContext();
    const { platformFeatures } = usePlanContext();
    const { translateString } = useTranslation();

    const url = isNullOrWhitespace(customApiUrl ?? null) ? `/relations/${forEntityId}/tasks` : customApiUrl!;
    const { isLoading, data, refetch } = useDotLegalQuery<Array<RelatedTaskViewModel>>(url, {
        enabled: permissions.taskPermissions.read && platformFeatures.canAccessRelationsBetweenEntities,
    });

    let numberOfTasks = isLoading ? undefined : data?.length;
    const getTaskTabHeader = () => {
        return `${translateString("tasks")} ${numberOfTasks !== undefined && numberOfTasks > 0 ? `(${numberOfTasks})` : ""}`;
    };

    return {
        isLoading,
        data,
        refetch,
        count: numberOfTasks,
        getTaskTabHeader,
    };
}
